import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { useLanguage } from "@/i18n";
import { updateConsent } from "@/analytics";

const STORAGE_KEY = "edilcomplex-cookie-consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const choice = localStorage.getItem(STORAGE_KEY);
    if (choice === "granted") {
      updateConsent("granted");
    } else if (choice !== "denied") {
      const timer = setTimeout(() => setVisible(true), 1600);
      return () => clearTimeout(timer);
    }
  }, []);

  const choose = (value) => {
    localStorage.setItem(STORAGE_KEY, value);
    updateConsent(value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[70] border-t border-stone-300 bg-[#FFFFFF]/95 backdrop-blur-xl md:bottom-6 md:left-6 md:right-auto md:max-w-md md:border"
        >
          <div className="p-6">
            <div className="flex items-start gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C89D66]/40 text-[#8F6B3C]">
                <Cookie className="h-4 w-4" />
              </span>
              <div>
                <p className="font-serif text-lg text-stone-900">{t("cookieTitle")}</p>
                <p className="mt-2 text-xs leading-relaxed text-stone-500">
                  {t("cookieText")}{" "}
                  <Link
                    to="/cookie-policy"
                    data-testid="cookie-banner-privacy-link"
                    className="text-[#8F6B3C] underline underline-offset-2 hover:text-[#7A5A30]"
                  >
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button
                data-testid="cookie-banner-reject-btn"
                onClick={() => choose("denied")}
                className="flex-1 border border-stone-300 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-800 transition-colors duration-300 hover:border-[#8F6B3C] hover:text-[#8F6B3C]"
              >
                {t("cookieReject")}
              </button>
              <button
                data-testid="cookie-banner-accept-btn"
                onClick={() => choose("granted")}
                className="flex-1 bg-[#C89D66] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0F1012] transition-colors duration-300 hover:bg-[#D4A972]"
              >
                {t("cookieAccept")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
